import React, { useState, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import OrganizationSubscriptionsAPI from "../../API/organizationSubscriptions.api";
import { SuccessAlert } from "../../utils/Alerts";
import BiasCorrectionForm from "../BiasCorrectionSteps/BiasCorrectionForm";
import { getCorrectionChanges } from "../../utils/BiasCorrectionsHelpers";
import BaseMultistepModal from "./BaseMultistepModal";
import { Trans } from "react-i18next";
import SelectParameterForm from "../Forms/SelectParameterForm";

const SubscribedBiasCorrection = ({
    isVisible,
    setIsVisible,
    selectedProduct,
    isLoading,
    selectedProductDetails,
    getSubscriptionsDetails,
}) => {
    const [productParameters, setProductParameters] = useState({});
    const [selectedBundleProduct, setSelectedBundleProduct] = useState(null);
    const [selectedParameter, setSelectedParameter] = useState(null);
    const [selectedBiasCorrection, setSelectedBiasCorrection] = useState([]);
    const [referenceBiasCorrection, setReferenceBiasCorrection] = useState({});

    // const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const multiStepModalRef = useRef();
    const { t } = useTranslation();

    useEffect(() => {
        if (!isVisible) {
            resetForm();
        }
    }, [isVisible]);

    useEffect(() => {
        if (selectedParameter?.biasCorrections) {
            mapCorrections();
        }
    }, [selectedParameter]);

    const mapCorrections = () => {
        const biasReference = {};
        selectedParameter.biasCorrections.forEach((correction) => {
            biasReference[correction.value] = correction;
        });

        setSelectedBiasCorrection(selectedParameter.biasCorrections);
        setReferenceBiasCorrection(biasReference);
    };

    const resetForm = () => {
        setSelectedBundleProduct(null);
        setSelectedParameter(null);
        setSelectedBiasCorrection([]);
        setReferenceBiasCorrection({});
    };

    const getPayload = () => {
        const isBundle = selectedProductDetails.isBundle;
        const subscriptionMaterialId = isBundle
            ? selectedBundleProduct.subscriptionMaterialId
            : selectedProductDetails.subscriptionMaterialId;
        return {
            modelParameterProductVersionId: selectedParameter?.value,
            organizationSubscriptionId: selectedProduct?.id,
            biasCorrections: getCorrectionChanges(
                selectedBiasCorrection,
                referenceBiasCorrection
            ),
            subscriptionMaterialId,
        };
    };

    const isSubmitButtonDisabled = () => {
        return (
            getCorrectionChanges(
                selectedBiasCorrection,
                referenceBiasCorrection
            ).length < 1
        );
    };

    const submitBiasCorrections = async (buttonIndex) => {
        setIsSubmitting(buttonIndex);
        const result = await OrganizationSubscriptionsAPI.updateBiasCorrections(
            getPayload()
        );

        if (result?.modified) {
            SuccessAlert(t("biasCorrectionUpdateSuccessMessage"));
            getSubscriptionsDetails();
        }
        setIsSubmitting(false);
    };

    const getSubmitButtonContent = (defaultContent, index) => {
        return isSubmitting === index ? (
            <>
                <span className="animate-spin mr-3 inline-flex">
                    <i className="fas fa-sync"></i>
                </span>
                <Trans i18nKey="requesting">Requesting...</Trans>
            </>
        ) : (
            defaultContent
        );
    };

    const getSteps = () => {
        return [
            {
                name: t("selectParameter"),
                content: () => {
                    return (
                        <div className="px-4">
                            <SelectParameterForm
                                isBundle={selectedProductDetails.isBundle}
                                bundleProducts={
                                    selectedProductDetails.bundleProducts
                                }
                                parameters={selectedProductDetails.parameters}
                                selectedBundleProduct={selectedBundleProduct}
                                setSelectedBundleProduct={
                                    setSelectedBundleProduct
                                }
                                selectedParameter={selectedParameter}
                                setSelectedParameter={setSelectedParameter}
                                productParameters={productParameters}
                                setProductParameters={setProductParameters}
                            />
                        </div>
                    );
                },
                action: {
                    name: t("next"),
                    disabled: !selectedParameter,
                    submitAction: () => multiStepModalRef.current.nextPage(),
                },
            },
            {
                name: t("biasCorrection"),
                content: () => {
                    return (
                        <div className="p-3">
                            <BiasCorrectionForm
                                modelId={selectedProductDetails.value}
                                isVisible={isVisible}
                                addedCorrections={selectedBiasCorrection}
                                setAddedCorrections={setSelectedBiasCorrection}
                                referenceCorrections={referenceBiasCorrection}
                            />
                        </div>
                    );
                },
                action: {
                    name: getSubmitButtonContent(t("save"), 1),
                    disabled: isSubmitButtonDisabled() || isSubmitting,
                    isSubmitting,
                    submitAction: () => {
                        submitBiasCorrections(1).then(() =>
                            setIsVisible(false)
                        );
                    },
                },
                subAction: {
                    name: getSubmitButtonContent(t("saveAddMore"), 2),
                    disabled: isSubmitButtonDisabled() || isSubmitting,
                    isSubmitting,
                    submitAction: () => {
                        submitBiasCorrections(2).then(() => {
                            resetForm();
                            multiStepModalRef.current.previousPage();
                        });
                    },
                },
            },
        ];
    };

    return (
        <BaseMultistepModal
            ref={multiStepModalRef}
            isVisible={isVisible}
            setIsVisible={setIsVisible}
            title={
                <>
                    {`${t("biasCorrectionFor")} `}
                    <strong>{selectedProduct?.productName}</strong>
                </>
            }
            isMedium={true}
            steps={getSteps()}
            isLoading={isLoading}
        />
    );
};

export default SubscribedBiasCorrection;
